import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { EditableCell, StatusPill } from '../components/InlineEditTable';
import { useBulkSelect } from '../hooks/useBulkSelect';
import SearchableSelect from '../components/SearchableSelect';
import DataTable from '../components/DataTable';
import FormField from '../components/FormField';
import { useFormValidation } from '../hooks/useFormValidation';
import { useSoftDelete } from '../hooks/useSoftDelete';
import useSort from '../hooks/useSort';
import Toast from '../components/Toast';
import { useCurrentUser } from '../context/UserContext';
import { getLeadStatuses, LEAD_STATUS_COLORS } from '../utils/leadStatus';
import CollapsibleSection from '../components/CollapsibleSection';
import { neededCount, percentComplete } from '../utils/goalProgress';
import { buildStatusGroupMap, splitByStatusGroup, buildProgramOptionGroups, closedProgramIdSet, buildComponentOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;


const emptyForm = () => ({
  goal_name: '', program_id: '', component_id: '', supplier_id: '', status: '',
  quantity_ordered: '', spend_per: '', flight_date_start: '', flight_date_end: '',
  use_parent_targeting: false, notes: '',
});

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box' };

const GOAL_RULES = {
  goal_name: { required: true, label: 'Goal Name' },
  component_id: { required: true, label: 'Component' },
  flight_date_start: { required: true, label: 'Flight Start' },
  flight_date_end: { required: true, label: 'Flight End' },
};

export default function Goals() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [components, setComponents] = useState([]);
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on every Program/
  // Component picker below instead of mixing into the flat list — see
  // src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const programOptionGroups = buildProgramOptionGroups(programs, programStatusGroupMap);
  const closedProgramIds = closedProgramIdSet(programs, programStatusGroupMap);
  const [suppliers, setSuppliers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [targetingLists, setTargetingLists] = useState([]);

  // Filters
  const [filterProgram, setFilterProgram] = useState('');
  const [filterComponent, setFilterComponent] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const filterComponentOptionGroups = buildComponentOptionGroups(
    filterProgram ? components.filter(c => String(c.program_id) === filterProgram) : components,
    closedProgramIds,
  );
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  // Sort
  // Column-header sort, shared by the active and collapsed-Closed tables via
  // sortRows. No default key — until a header is clicked, both buckets keep
  // splitByStatusGroup's own flight_date_end-desc order.
  const { sortRows, field: sortField, dir: sortDir, toggle: handleSort } = useSort(null, null, 'asc', {
    program: g => programForComponent(g.component_id),
    component: g => componentName(g.component_id),
    supplier: g => supplierName(g.supplier_id),
  });

  // Form / editing
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);

  // Row expansion — targeting panel
  const [expandedRow, setExpandedRow] = useState(null);

  const [goalDetails, setGoalDetails] = useState({});

  // Leads expansion — independent from targeting, uses same inline display for both per-row and Show All
  const [expandedLeadsRows, setExpandedLeadsRows] = useState({});  // goal_id → bool
  const [leadsData, setLeadsData] = useState({});                  // goal_id → {total, by_status} | null

  const [showAssignModal, setShowAssignModal] = useState(null);
  const [assignListId, setAssignListId] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [assignTypeFilter, setAssignTypeFilter] = useState('');

  // Post-create component list prompt
  const [pendingAssignment, setPendingAssignment] = useState(null);
  // { goalId: int, lists: [{id, list_name, list_type}], selected: Set<int> }

  // Toggle the targeting panel for a row
  const toggleTargeting = (g) => {
    const id = g.id;
    if (expandedRow === id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
    }
  };

  // Lazy-load targeting detail when panel opens
  useEffect(() => {
    if (!expandedRow || goalDetails[expandedRow]) return;
    const id = expandedRow;
    axios.get(`${API}/goals/${id}`)
      .then(res => setGoalDetails(prev => ({ ...prev, [id]: res.data })))
      .catch(() => setGoalDetails(prev => ({ ...prev, [id]: { effective_targeting: [], targeting_assignments: [] } })));
  }, [expandedRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch leads for a single goal (lazy)
  const fetchLeadsForGoal = (goalId) => {
    if (leadsData[goalId] !== undefined) return;
    setLeadsData(prev => ({ ...prev, [goalId]: null })); // null = loading
    axios.get(`${API}/leads/summary?goal_id=${goalId}`)
      .then(res => {
        const entry = res.data.find(r => r.goal_id === goalId);
        setLeadsData(prev => ({ ...prev, [goalId]: entry || { total: 0, by_status: {} } }));
      })
      .catch(() => setLeadsData(prev => ({ ...prev, [goalId]: { total: 0, by_status: {} } })));
  };

  // Toggle leads inline row for a single goal
  const toggleLeadsRow = (goalId) => {
    const nowExpanding = !expandedLeadsRows[goalId];
    setExpandedLeadsRows(prev => ({ ...prev, [goalId]: nowExpanding }));
    if (nowExpanding) fetchLeadsForGoal(goalId);
  };

  // Show All / Hide All leads
  const anyLeadsShown = sortedGoals => sortedGoals.some(g => expandedLeadsRows[g.id]);

  const handleToggleAllLeads = async (visibleGoals) => {
    if (anyLeadsShown(visibleGoals)) {
      setExpandedLeadsRows({});
    } else {
      const newState = {};
      visibleGoals.forEach(g => { newState[g.id] = true; });
      setExpandedLeadsRows(newState);
      // Fetch missing leads all at once
      const missing = visibleGoals.filter(g => leadsData[g.id] === undefined);
      if (missing.length > 0) {
        setLeadsData(prev => {
          const upd = { ...prev };
          missing.forEach(g => { upd[g.id] = null; });
          return upd;
        });
        try {
          const res = await axios.get(`${API}/leads/summary`);
          const map = {};
          res.data.forEach(e => { map[e.goal_id] = e; });
          setLeadsData(prev => {
            const upd = { ...prev };
            missing.forEach(g => { upd[g.id] = map[g.id] || { total: 0, by_status: {} }; });
            return upd;
          });
        } catch {}
      }
    }
  };

  const { selected, toggle, toggleAll, clear } = useBulkSelect();
  const { errors, validate, reset: resetErrors } = useFormValidation(GOAL_RULES);

  // Columns: Program, Component, Goal Name, Supplier, Status, Qty, Spend, Flight Start, Flight End, Pending Review, Under Review, DA Accepted, % Complete, Client Accepted, Rejected, Needed, Actions, [Select]
  // pending_review_count/under_review_count are plain Lead.status counts now
  // (Lead.status is the single source of truth for a goal's bucket counts —
  // see app/utils/lead_status.py / goals.py's _attach_counts in the backend).

  useEffect(() => {
    axios.get(`${API}/components/`).then(r => setComponents(r.data));
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data));
    axios.get(`${API}/suppliers/`).then(r => setSuppliers(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=goal_status`).then(r => setStatuses(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
    axios.get(`${API}/targeting-lists/`).then(r => setTargetingLists(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchGoals(); }, [showDeleted, filterProgram, filterComponent, filterSupplier]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGoals = () => axios.get(`${API}/goals/`, { params: {
    include_counts: true,
    include_deleted: showDeleted,
    ...(filterProgram ? { program_id: filterProgram } : {}),
    ...(filterComponent ? { component_id: filterComponent } : {}),
    ...(filterSupplier ? { supplier_id: filterSupplier } : {}),
  } }).then(r => setGoals(r.data));

  const { toast, dismissToast, restore: restoreGoal, deleteWithUndo } = useSoftDelete('goal', fetchGoals);

  // Persist a single inline-edited field. Merge the patch locally instead of a full
  // fetchGoals() — a refetch re-runs the lead-count aggregation over every visible goal
  // for a one-field change. (The PUT response can't be used directly either: GoalOut's
  // count fields are only populated by list_goals' include_counts pass, not update_goal.)
  const saveField = async (id, patch) => {
    await axios.put(`${API}/goals/${id}`, patch);
    setGoals(prev => prev.map(g => {
      if (g.id !== id) return g;
      const merged = { ...g, ...patch };
      merged.needed_count = neededCount(merged);
      return merged;
    }));
  };

  const bulkSetStatus = async (status) => {
    await Promise.all([...selected].map(id => axios.put(`${API}/goals/${id}`, { status })));
    clear();
    await fetchGoals();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) return;
    const data = {
      ...form,
      component_id: form.component_id ? parseInt(form.component_id) : null,
      supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
      quantity_ordered: form.quantity_ordered !== '' ? parseInt(form.quantity_ordered) : null,
      spend_per: form.spend_per !== '' ? parseFloat(form.spend_per) : null,
      flight_date_start: form.flight_date_start || null,
      flight_date_end: form.flight_date_end || null,
      use_parent_targeting: false,  // deprecated — explicit goal-level assignment only
    };
    // Remove program_id before sending (not a goal field)
    delete data.program_id;
    try {
      if (editing) {
        await axios.put(`${API}/goals/${editing}`, data);
        setEditing(null);
        setForm(emptyForm());
        setShowForm(false);
        resetErrors();
        fetchGoals();
      } else {
        const res = await axios.post(`${API}/goals/`, data);
        const newGoal = res.data;
        setForm(emptyForm());
        setShowForm(false);
        resetErrors();
        fetchGoals();
        // Check if the parent component has staging lists to offer
        const compId = data.component_id;
        const comp = components.find(c => c.id === compId);
        const stagingLists = (comp?.targeting_assignments || [])
          .map(ta => ta.targeting_list)
          .filter(Boolean);
        if (stagingLists.length > 0) {
          setPendingAssignment({
            goalId: newGoal.id,
            goalName: newGoal.goal_name || `Goal #${newGoal.id}`,
            lists: stagingLists,
            selected: new Set(stagingLists.map(l => l.id)),
          });
        }
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      alert('Could not save goal: ' + (Array.isArray(d) ? d.map(x => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join('; ') : (d || err.message)));
    }
  };

  const handleConfirmPendingAssignment = async () => {
    if (!pendingAssignment) return;
    const { goalId, lists, selected } = pendingAssignment;
    for (const list of lists) {
      if (selected.has(list.id)) {
        await axios.post(`${API}/goals/${goalId}/targeting`, { targeting_list_id: list.id });
      }
    }
    setPendingAssignment(null);
    fetchGoals();
  };

  const handleEdit = (g) => {
    const comp = components.find(c => c.id === g.component_id);
    setEditing(g.id);
    setForm({
      goal_name: g.goal_name || '',
      program_id: comp ? String(comp.program_id) : '',
      component_id: g.component_id || '',
      supplier_id: g.supplier_id || '',
      status: g.status || '',
      quantity_ordered: g.quantity_ordered != null ? g.quantity_ordered : '',
      spend_per: g.spend_per != null ? g.spend_per : '',
      flight_date_start: g.flight_date_start || '',
      flight_date_end: g.flight_date_end || '',
      use_parent_targeting: g.use_parent_targeting || false,
      notes: g.notes || '',
    });
    setShowForm(true);
    resetErrors();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClone = async (g) => {
    const data = {
      goal_name: (g.goal_name || 'Goal') + ' (copy)',
      component_id: g.component_id,
      supplier_id: g.supplier_id,
      status: g.status,
      quantity_ordered: g.quantity_ordered,
      spend_per: g.spend_per,
      flight_date_start: g.flight_date_start,
      flight_date_end: g.flight_date_end,
      use_parent_targeting: g.use_parent_targeting,
      notes: g.notes,
    };
    await axios.post(`${API}/goals/`, data);
    fetchGoals();
  };

  const handleDelete = async (g) => {
    if (window.confirm('Delete this goal?')) {
      await deleteWithUndo(g, g.goal_name || 'goal', () => axios.delete(`${API}/goals/${g.id}`));
    }
  };

  const handleAssignTargeting = async () => {
    if (!assignListId) return;
    await axios.post(`${API}/goals/${showAssignModal}/targeting`, { targeting_list_id: parseInt(assignListId) });
    setShowAssignModal(null);
    setAssignListId('');
    fetchGoals();
  };

  const handleRemoveTargeting = async (goalId, assignmentId) => {
    await axios.delete(`${API}/goals/${goalId}/targeting/${assignmentId}`);
    fetchGoals();
  };

  const componentName = (id) => components.find(c => c.id === id)?.component_name || '—';
  const supplierName = (id) => suppliers.find(s => s.id === id)?.supplier_name || '—';
  const programForComponent = (compId) => {
    const comp = components.find(c => c.id === compId);
    if (!comp) return '—';
    return programs.find(p => p.id === comp.program_id)?.program_name || '—';
  };
  const programIdForComponent = (compId) => {
    const comp = components.find(c => c.id === compId);
    return comp?.program_id;
  };

  // Components filtered by selected program in the form
  const formComponents = form.program_id
    ? components.filter(c => String(c.program_id) === String(form.program_id))
    : components;
  const formComponentOptionGroups = buildComponentOptionGroups(formComponents, closedProgramIds);

  // Apply filters
  const filtered = goals.filter(g => {
    if (filterProgram) {
      const progId = programIdForComponent(g.component_id);
      if (String(progId) !== filterProgram) return false;
    }
    if (filterComponent && String(g.component_id) !== filterComponent) return false;
    if (filterSupplier && String(g.supplier_id) !== filterSupplier) return false;
    if (filterStatus && g.status !== filterStatus) return false;
    return true;
  });

  // Pending/Running always visible, Closed collapsed at the bottom — classification
  // comes from lookup_values.status_group (admin-editable), not a hardcoded status
  // list. An explicit column-header sort click still applies within each bucket; the
  // group split and the Closed collapse stay in effect either way.
  const statusGroupMap = buildStatusGroupMap(statuses);
  const { active: activeBase, closed: closedBase } = splitByStatusGroup(
    filtered, { statusKey: 'status', groupMap: statusGroupMap, dateKey: 'flight_date_end' }
  );
  const sortedGoals = sortRows(activeBase);
  const sortedClosedGoals = sortRows(closedBase);

  // Inline-edit option lists
  const componentOpts = components.map(c => ({ value: c.id, label: c.component_name }));
  const supplierOpts = [{ value: '', label: 'None' }, ...suppliers.map(s => ({ value: s.id, label: s.supplier_name }))];
  const statusOpts = [{ value: '', label: '—' }, ...statuses.map(s => ({ value: s.value, label: s.value }))];

  const numCell = (val, style) => <div style={{ textAlign: 'right', ...style }}>{val}</div>;

  const columns = [
    { key: 'program', header: 'Program', width: 140, sortable: true, render: g => programForComponent(g.component_id) },
    { key: 'component', header: 'Component', width: 160, sortable: true, render: g =>
        <EditableCell type="select" value={g.component_id} options={componentOpts} display={componentName(g.component_id)}
          onSave={v => saveField(g.id, { component_id: v ? parseInt(v) : null })} /> },
    { key: 'goal_name', header: 'Goal Name', width: 160, sortable: true, render: g =>
        <EditableCell value={g.goal_name} display={<strong style={{ color: '#2c3e50' }}>{g.goal_name || '(unnamed)'}</strong>}
          onSave={v => saveField(g.id, { goal_name: v })} /> },
    { key: 'supplier', header: 'Supplier', width: 130, sortable: true, render: g =>
        <EditableCell type="select" value={g.supplier_id} options={supplierOpts} display={supplierName(g.supplier_id)}
          onSave={v => saveField(g.id, { supplier_id: v ? parseInt(v) : null })} /> },
    { key: 'status', header: 'Status', width: 100, sortable: true, render: g =>
        <EditableCell type="select" value={g.status} options={statusOpts}
          display={<StatusPill value={g.status} lookups={statuses} fallback="#27ae60" />}
          onSave={v => saveField(g.id, { status: v })} /> },
    { key: 'quantity_ordered', header: 'Qty', width: 90, sortable: true, render: g =>
        <EditableCell type="number" value={g.quantity_ordered} onSave={v => saveField(g.id, { quantity_ordered: v === '' ? null : parseInt(v) })} /> },
    { key: 'spend_per', header: 'Spend/Lead', width: 90, sortable: true, render: g =>
        <EditableCell type="number" step="0.01" value={g.spend_per} display={g.spend_per ? '$' + g.spend_per : undefined}
          onSave={v => saveField(g.id, { spend_per: v === '' ? null : parseFloat(v) })} /> },
    { key: 'flight_date_start', header: 'Flight Start', width: 150, sortable: true, render: g =>
        <EditableCell type="date" value={g.flight_date_start} onSave={v => saveField(g.id, { flight_date_start: v || null })} /> },
    { key: 'flight_date_end', header: 'Flight End', width: 150, sortable: true, render: g =>
        <EditableCell type="date" value={g.flight_date_end} onSave={v => saveField(g.id, { flight_date_end: v || null })} /> },
    { key: 'pending_review_count', header: 'Pending Review', width: 90, sortable: true, render: g => numCell(g.pending_review_count ?? '—', { color: '#888' }) },
    { key: 'under_review_count', header: 'Under Review', width: 90, sortable: true, render: g => numCell(g.under_review_count ?? '—', { color: '#b7950b' }) },
    { key: 'da_accepted_count', header: 'DA Accepted', width: 110, sortable: true, render: g => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ minWidth: '24px', textAlign: 'right', color: '#1e8449', fontWeight: '600' }}>{g.da_accepted_count ?? '—'}</span>
          {g.quantity_ordered > 0 && g.da_accepted_count != null && (
            <div style={{ flex: 1, height: '6px', background: '#e0e0e0', borderRadius: '3px', minWidth: '30px' }}>
              <div style={{ height: '6px', borderRadius: '3px', background: g.da_accepted_count >= g.quantity_ordered ? '#27ae60' : '#3498db', width: Math.min(100, Math.round((g.da_accepted_count / g.quantity_ordered) * 100)) + '%' }} />
            </div>
          )}
        </div>
      ) },
    { key: 'pct_complete', header: '% Complete', width: 90, render: g => numCell(
        percentComplete(g) != null ? percentComplete(g) + '%' : '—',
        { color: percentComplete(g) >= 100 ? '#1e8449' : '#555', fontWeight: '500' }) },
    { key: 'client_accepted_count', header: 'Client Accepted', width: 110, sortable: true, render: g => numCell(g.client_accepted_count ?? '—', { color: '#2471a3' }) },
    { key: 'rejected_count', header: 'Rejected', width: 80, sortable: true, render: g => numCell(g.rejected_count ?? '—', { color: g.rejected_count > 0 ? '#c0392b' : '#888' }) },
    { key: 'needed_count', header: 'Needed', width: 80, sortable: true, render: g => numCell(g.needed_count != null ? g.needed_count : '—', { color: g.needed_count === 0 ? '#1e8449' : '#e67e22', fontWeight: g.needed_count === 0 ? '600' : 'normal' }) },
    { key: 'actions', header: 'Actions', width: 250, nowrap: true, render: g => {
        if (g.deleted_at) {
          return <button onClick={() => restoreGoal(g)} style={{ padding: '4px 7px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Restore</button>;
        }
        const targetingOpen = expandedRow === g.id;
        const leadsOpen = !!expandedLeadsRows[g.id];
        const targBtn = { padding: '4px 9px', fontSize: '12px', border: 'none', cursor: 'pointer', borderRadius: '4px', background: targetingOpen ? '#2c3e50' : '#e8ecef', color: targetingOpen ? 'white' : '#555' };
        const leadsBtn = { padding: '4px 9px', fontSize: '12px', border: 'none', cursor: 'pointer', borderRadius: '4px', background: leadsOpen ? '#27ae60' : '#e8ecef', color: leadsOpen ? 'white' : '#555' };
        return (
          <>
            <button onClick={() => toggleTargeting(g)}        style={{ ...targBtn,  marginRight: '3px' }}>🎯</button>
            <button onClick={() => toggleLeadsRow(g.id)}      style={{ ...leadsBtn, marginRight: '3px' }}>📊</button>
            <button onClick={() => navigate(`/lead-upload?goal_id=${g.id}`)} style={{ marginRight: '3px', padding: '4px 7px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>📤</button>
            <button onClick={() => handleClone(g)}            style={{ marginRight: '3px', padding: '4px 7px', background: '#e67e22', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Clone</button>
            <button onClick={() => handleEdit(g)}             style={{ marginRight: '3px', padding: '4px 7px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
            <button onClick={() => handleDelete(g)}        style={{ padding: '4px 7px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Del</button>
          </>
        );
      } },
  ];

  const renderExpanded = (g) => {
    const targetingOpen = expandedRow === g.id;
    const leadsOpen = !!expandedLeadsRows[g.id];
    if (!targetingOpen && !leadsOpen) return null;
    const detail = goalDetails[g.id];
    const leadSummary = leadsData[g.id];
    return (
      <>
        {leadsOpen && (
          <div style={{ padding: '8px 20px 12px', background: '#f5fdf5', borderBottom: '1px solid #c8e6c9' }}>
            <LeadsInlineRow leadSummary={leadSummary} navigate={navigate} goalId={g.id} />
          </div>
        )}
        {targetingOpen && (
          <div style={{ padding: '12px 20px', background: '#f8fbff', borderBottom: '2px solid #e0ecff' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Targeting Lists</div>
            {g.use_parent_targeting && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef9e7', border: '1px solid #f7dc6f', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', color: '#7d6608', marginBottom: '10px' }}>
                ⬆️ Using parent component's targeting lists
              </div>
            )}
            {detail ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                {(detail.effective_targeting || []).map((tl, i) => (
                  <span key={i} style={{ background: '#eaf2fb', color: '#2c3e50', borderRadius: '12px', padding: '4px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {tl.list_name}
                    <span style={{ fontSize: '10px', background: '#3498db', color: 'white', borderRadius: '8px', padding: '1px 5px' }}>{tl.list_type}</span>
                  </span>
                ))}
                {(detail.effective_targeting || []).length === 0 && (
                  <span style={{ color: '#aaa', fontSize: '12px' }}>No targeting lists</span>
                )}
              </div>
            ) : (
              <div style={{ color: '#aaa', fontSize: '12px' }}>Loading…</div>
            )}
            {!g.use_parent_targeting && (
              <>
                {detail && (detail.targeting_assignments || []).map(ta => (
                  <span key={ta.id} style={{ background: '#eaf2fb', color: '#2c3e50', borderRadius: '12px', padding: '4px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginRight: '6px', marginBottom: '6px' }}>
                    {ta.targeting_list?.list_name}
                    <button onClick={() => handleRemoveTargeting(g.id, ta.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '12px', padding: 0, lineHeight: 1 }}>×</button>
                  </span>
                ))}
                <div style={{ marginTop: '8px' }}>
                  <button onClick={() => { setShowAssignModal(g.id); setAssignListId(''); setAssignSearch(''); setAssignTypeFilter(''); }} style={{ padding: '5px 12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>+ Assign List</button>
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Goals</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Program filter */}
          <SearchableSelect value={filterProgram} placeholder="All Programs"
            onChange={v => { setFilterProgram(v); setFilterComponent(''); }}
            options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions} />
          {/* Component filter */}
          <SearchableSelect value={filterComponent} placeholder="All Components"
            onChange={setFilterComponent}
            options={filterComponentOptionGroups.options}
            closedOptions={filterComponentOptionGroups.closedOptions} />
          {/* Supplier filter */}
          <SearchableSelect value={filterSupplier} placeholder="All Suppliers"
            onChange={setFilterSupplier}
            options={suppliers.map(s => ({ value: String(s.id), label: s.supplier_name }))} />
          {/* Status filter */}
          <SearchableSelect value={filterStatus} placeholder="All Statuses"
            onChange={setFilterStatus}
            options={statuses.map(s => ({ value: s.value, label: s.value }))} />
          {isAdmin && (
            <label style={{ fontSize: '13px', color: '#555', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
              Show deleted
            </label>
          )}
          {/* Show All / Hide All Leads toggle */}
          <button
            onClick={() => handleToggleAllLeads(sortedGoals)}
            style={{
              padding: '7px 14px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
              background: anyLeadsShown(sortedGoals) ? '#27ae60' : '#e8ecef',
              color: anyLeadsShown(sortedGoals) ? 'white' : '#555',
              fontWeight: anyLeadsShown(sortedGoals) ? '600' : 'normal',
            }}
          >
            📊 {anyLeadsShown(sortedGoals) ? 'Hide Leads' : 'Show Leads'}
          </button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm()); resetErrors(); }}
            style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + New Goal
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px' }}>{editing ? 'Edit Goal' : 'New Goal'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <FormField label="Goal Name" required error={errors.goal_name}>
              <input value={form.goal_name} onChange={e => setForm({ ...form, goal_name: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Program">
              <SearchableSelect value={form.program_id}
                onChange={v => {
                  const pid = v;
                  // If current component is from a different program, clear it
                  const comp = components.find(c => c.id === parseInt(form.component_id));
                  const newCompId = comp && String(comp.program_id) === pid ? form.component_id : '';
                  setForm({ ...form, program_id: pid, component_id: newCompId });
                }}
                options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
                placeholder="All Programs" />
            </FormField>
            <FormField label="Component" required error={errors.component_id}>
              <SearchableSelect value={form.component_id}
                onChange={v => setForm({ ...form, component_id: v })}
                options={formComponentOptionGroups.options}
                closedOptions={formComponentOptionGroups.closedOptions}
                placeholder="Select component…" />
            </FormField>
            <FormField label="Supplier">
              <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} style={inputStyle}>
                <option value="">None</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                <option value="">Select…</option>
                {statuses.map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
              </select>
            </FormField>
            <FormField label="Qty Ordered">
              <input type="number" value={form.quantity_ordered} onChange={e => setForm({ ...form, quantity_ordered: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Spend/Lead">
              <input type="number" step="0.01" value={form.spend_per} onChange={e => setForm({ ...form, spend_per: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Flight Start" required error={errors.flight_date_start}>
              <input type="date" value={form.flight_date_start} onChange={e => setForm({ ...form, flight_date_start: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Flight End" required error={errors.flight_date_end}>
              <input type="date" value={form.flight_date_end} onChange={e => setForm({ ...form, flight_date_end: e.target.value })} style={inputStyle} />
            </FormField>
            {/* Targeting lists are assigned explicitly after creation — no parent inheritance */}
            <FormField label="Notes" gridColumn="span 2">
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                style={{ ...inputStyle, height: '60px', resize: 'vertical' }} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {editing ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm()); resetErrors(); }}
              style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <DataTable
        rows={sortedGoals}
        columns={columns}
        sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
        bulk={{ selected, toggle, toggleAll, statuses, onApply: bulkSetStatus }}
        renderExpanded={renderExpanded}
        rowStyle={g => ({ background: g.deleted_at ? '#f7f7f7' : ((expandedRow === g.id || expandedLeadsRows[g.id] || selected.has(g.id)) ? '#f0f7ff' : 'white'), opacity: g.deleted_at ? 0.6 : 1 })}
        emptyText="No goals found."
      />
      {sortedClosedGoals.length > 0 && (
        <CollapsibleSection label="Closed" count={sortedClosedGoals.length} bodyStyle={{ padding: 0 }}>
          <DataTable
            rows={sortedClosedGoals}
            columns={columns}
            sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
            bulk={{ selected, toggle, toggleAll, statuses, onApply: bulkSetStatus }}
            renderExpanded={renderExpanded}
            rowStyle={g => ({ background: g.deleted_at ? '#f7f7f7' : ((expandedRow === g.id || expandedLeadsRows[g.id] || selected.has(g.id)) ? '#f0f7ff' : 'white'), opacity: g.deleted_at ? 0.6 : 1 })}
            emptyText="No closed goals."
          />
        </CollapsibleSection>
      )}

      {showAssignModal && (() => {
        const alreadyAssigned = new Set(
          (goalDetails[showAssignModal]?.targeting_assignments || []).map(ta => ta.targeting_list?.id).filter(Boolean)
        );
        const uniqueTypes = [...new Set(targetingLists.map(l => l.list_type).filter(Boolean))].sort();
        const filtered = targetingLists.filter(tl => {
          if (assignTypeFilter && tl.list_type !== assignTypeFilter) return false;
          if (assignSearch && !tl.list_name.toLowerCase().includes(assignSearch.toLowerCase())) return false;
          return true;
        });
        const selected = targetingLists.find(l => String(l.id) === String(assignListId));
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: '8px', width: '520px', maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>

              {/* Header */}
              <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', color: '#2c3e50' }}>Assign Targeting List</h3>
                  <button onClick={() => setShowAssignModal(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: 0 }}>×</button>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    autoFocus
                    placeholder="Search lists…"
                    value={assignSearch}
                    onChange={e => { setAssignSearch(e.target.value); setAssignListId(''); }}
                    style={{ ...inputStyle, flex: 1, fontSize: '13px' }}
                  />
                  <select value={assignTypeFilter} onChange={e => { setAssignTypeFilter(e.target.value); setAssignListId(''); }}
                    style={{ ...inputStyle, width: '140px', fontSize: '13px' }}>
                    <option value="">All Types</option>
                    {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* List */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {filtered.length === 0 && (
                  <div style={{ padding: '32px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>No lists match your search.</div>
                )}
                {filtered.map(tl => {
                  const isAssigned = alreadyAssigned.has(tl.id);
                  const isSelected = String(tl.id) === String(assignListId);
                  return (
                    <div key={tl.id}
                      onClick={() => !isAssigned && setAssignListId(String(tl.id))}
                      style={{
                        padding: '10px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: isAssigned ? 'default' : 'pointer',
                        background: isSelected ? '#e8f5e9' : isAssigned ? '#fafafa' : 'white',
                        opacity: isAssigned ? 0.6 : 1,
                        display: 'flex', alignItems: 'center', gap: '10px',
                      }}
                      onMouseEnter={e => { if (!isAssigned && !isSelected) e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { if (!isAssigned && !isSelected) e.currentTarget.style.background = 'white'; }}
                    >
                      {/* Selection indicator */}
                      <div style={{ width: '16px', flexShrink: 0, textAlign: 'center', fontSize: '14px', color: '#27ae60' }}>
                        {isSelected ? '●' : isAssigned ? '✓' : ''}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '3px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '500', color: '#2c3e50' }}>{tl.list_name}</span>
                          {tl.needs_review && <span style={{ background: '#e67e22', color: 'white', borderRadius: '3px', padding: '1px 5px', fontSize: '10px' }}>Needs review</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#888' }}>
                          <span style={{ background: '#e8ecef', borderRadius: '3px', padding: '1px 6px', color: '#555' }}>{tl.list_type}</span>
                          {tl.match_field && <span style={{ fontFamily: 'monospace', background: '#f0f4f8', padding: '1px 5px', borderRadius: '3px', color: '#2c3e50' }}>{tl.match_field}</span>}
                          <span>{tl.item_count?.toLocaleString()} items</span>
                          {isAssigned && <span style={{ color: '#27ae60', fontWeight: '600' }}>Already assigned</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{ padding: '14px 20px', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button onClick={handleAssignTargeting} disabled={!assignListId}
                  style={{ padding: '8px 18px', background: assignListId ? '#27ae60' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: assignListId ? 'pointer' : 'default', fontSize: '13px' }}>
                  {selected ? `Assign "${selected.list_name}"` : 'Select a list above'}
                </button>
                <button onClick={() => setShowAssignModal(null)}
                  style={{ padding: '8px 14px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                  Cancel
                </button>
                <span style={{ fontSize: '12px', color: '#aaa', marginLeft: 'auto' }}>{filtered.length} list{filtered.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Post-create: offer component staging lists ── */}
      {pendingAssignment && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '28px', width: '440px', maxWidth: '95vw' }}>
            <h3 style={{ margin: '0 0 8px', color: '#2c3e50' }}>Assign Targeting Lists</h3>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#555' }}>
              This component has targeting lists staged for its goals. Select which should be assigned to <strong>{pendingAssignment.goalName}</strong>:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {pendingAssignment.lists.map(list => (
                <label key={list.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', border: '1px solid #e8ecef', borderRadius: '6px', cursor: 'pointer', background: pendingAssignment.selected.has(list.id) ? '#f0f7ff' : 'white' }}>
                  <input type="checkbox"
                    checked={pendingAssignment.selected.has(list.id)}
                    onChange={() => {
                      const next = new Set(pendingAssignment.selected);
                      next.has(list.id) ? next.delete(list.id) : next.add(list.id);
                      setPendingAssignment({ ...pendingAssignment, selected: next });
                    }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#2c3e50' }}>{list.list_name}</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                      {list.list_type}{list.match_field ? ` · matches ${list.match_field}` : ''}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleConfirmPendingAssignment}
                style={{ padding: '8px 18px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                Assign Selected ({pendingAssignment.selected.size})
              </button>
              <button onClick={() => setPendingAssignment(null)}
                style={{ padding: '8px 14px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                Skip — assign later
              </button>
            </div>
          </div>
        </div>
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}

// ── Unified leads inline row — triggered by per-row 📊 button OR global Show All ──
function LeadsInlineRow({ goalId, leadSummary, navigate }) {
  if (leadSummary === null) {
    return <span style={{ fontSize: '12px', color: '#aaa' }}>Loading…</span>;
  }
  if (!leadSummary || leadSummary.total === 0) {
    return (
      <span style={{ fontSize: '12px', color: '#bbb' }}>
        No leads yet —{' '}
        <button onClick={() => navigate(`/lead-upload?goal_id=${goalId}`)}
          style={{ background: 'none', border: 'none', color: '#8e44ad', cursor: 'pointer', fontSize: '12px', padding: 0, textDecoration: 'underline' }}>
          Upload
        </button>
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      <span style={{ fontSize: '12px', color: '#555', fontWeight: '600' }}>{leadSummary.total.toLocaleString()} leads:</span>
      {/* Superset order (includes "Approved Pending Call Review") regardless of this
          goal's own program flag — a status with no leads simply doesn't render below,
          so this only prevents silently hiding real data for call-review programs. */}
      {getLeadStatuses(true).filter(s => leadSummary.by_status[s]).map(s => {
        const col = LEAD_STATUS_COLORS[s];
        return (
          <span key={s} style={{
            background: col.bg, border: `1px solid ${col.border}`, color: col.color,
            borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: '600',
          }}>
            {s}: {leadSummary.by_status[s].toLocaleString()}
          </span>
        );
      })}
      <button onClick={() => navigate(`/lead-upload?goal_id=${goalId}`)}
        style={{ marginLeft: '4px', padding: '3px 8px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
        📤 Upload
      </button>
    </div>
  );
}
